<script setup lang="ts">
import { ElIcon } from 'element-plus'
import { computed, type Ref, ref, watch } from 'vue'

import { Card, FlexComponent, InputComponent, PaginationComponent, TypographyComponent } from '../../'
import { SearchIcon } from '../../../assets'
import { formatNumber } from '../../../formatters'
import type { SegmentCriteriaDto, UserRefDto } from '../../../generated/social-media/metapicSocialMediaAPIDocumentation.schemas'
import type { GetSegmentCreatorLink, SegmentCreator, SegmentPreviewFilter, UseGetSegmentPreviewReturn } from '../../../types'
import { debounce } from '../../../utils'

import { SEGMENT_PREVIEW_TITLE } from './SegmentPreview.helpers'
import SegmentPreviewList from './SegmentPreviewList.vue'

const props = withDefaults(
  defineProps<{
    advertiserGroupId?: number
    loadPreview: (filterRef: Ref<SegmentPreviewFilter>) => UseGetSegmentPreviewReturn
    criteria?: SegmentCriteriaDto | null
    manualMembers?: UserRefDto[]
    excludedMembers?: UserRefDto[]
    isRemovable?: boolean
    getCreatorLink?: GetSegmentCreatorLink | null
    pageSize?: number
    segmentId?: string | null
  }>(),
  {
    advertiserGroupId: undefined,
    criteria: null,
    manualMembers: () => [],
    excludedMembers: () => [],
    isRemovable: false,
    getCreatorLink: null,
    pageSize: 10,
    segmentId: null,
  },
)
const emits = defineEmits<{ remove: [creator: SegmentCreator] }>()

const SEARCH_DEBOUNCE_MS = 300

const page = ref(1)
const query = ref('')
const handleSearchQuery = debounce((value: string) => {
  query.value = value
}, SEARCH_DEBOUNCE_MS)

const filter = computed<SegmentPreviewFilter>(() => ({
  advertiserGroupId: props.advertiserGroupId,
  segmentId: props.segmentId ?? undefined,
  criteria: props.criteria,
  manualMembers: props.manualMembers,
  excludedMembers: props.excludedMembers,
  query: query.value,
  page: page.value,
  size: props.pageSize,
}))

watch(
  () => [props.advertiserGroupId, props.segmentId, props.criteria, props.manualMembers, props.excludedMembers, query.value],
  () => {
    page.value = 1
  },
  { deep: true },
)

const { data, totalMembers, totalPages, exceedsMemberCap, isLoading, refetch } = props.loadPreview(filter)

const formattedCount = computed(() => formatNumber(totalMembers.value ?? 0))
const countLabel = computed(() => (totalMembers.value === 1 ? 'matching creator' : 'matching creators'))

defineExpose({ refetch })
</script>

<template>
  <Card :title="SEGMENT_PREVIEW_TITLE">
    <template #headerEnd>
      <!-- One line of body-medium: the count is semibold, the label regular (see the Figma character runs). -->
      <TypographyComponent variant="body-medium" color="var(--secondary-48)">
        <span class="segment-preview-results__count">{{ formattedCount }}</span> {{ countLabel }}
      </TypographyComponent>
    </template>

    <FlexComponent direction="column" :gap="16">
      <TypographyComponent v-if="exceedsMemberCap" variant="label-small" color="var(--alert)">
        The criteria match more creators than a refresh would add to the segment.
      </TypographyComponent>

      <InputComponent placeholder="Search creators" size="small" is-clearable @input="handleSearchQuery">
        <template #prefix>
          <ElIcon :size="18">
            <SearchIcon />
          </ElIcon>
        </template>
      </InputComponent>

      <SegmentPreviewList
        :creators="data"
        :is-loading="isLoading"
        :is-removable="isRemovable"
        :get-creator-link="getCreatorLink"
        :is-search-state="!!query"
        @remove="emits('remove', $event)"
      />
    </FlexComponent>

    <template #footer>
      <PaginationComponent v-if="!isLoading" v-model:current-page="page" :total-pages="totalPages" />
    </template>
  </Card>
</template>

<style scoped lang="scss">
.segment-preview-results__count {
  font-weight: 600;
}
</style>
